import { useState, useRef } from 'react';
import { 
  User, Bot, Upload, Link2, Image, 
  Loader2, Check, AlertCircle 
} from 'lucide-react';
import { fileService } from '../../services/PlatformService';

const AgentIdentityCard = ({ 
  formData, 
  onFieldChange 
}) => {
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Por favor, selecione uma imagem válida (JPEG, PNG, WebP ou GIF)');
      setTimeout(() => setUploadError(null), 3000);
      return;
    }

    // Validar tamanho (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setUploadError('A imagem deve ter no máximo 5MB');
      setTimeout(() => setUploadError(null), 3000);
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const response = await fileService.uploadFile(file);
      
      if (response.data && response.data.file_url) {
        // Atualiza a URL da foto
        onFieldChange('logo_url', response.data.file_url, false);
        setUploadSuccess(true);
        
        // Remove mensagem de sucesso após 3 segundos
        setTimeout(() => setUploadSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      setUploadError('Erro ao fazer upload da imagem. Tente novamente.');
      setTimeout(() => setUploadError(null), 3000);
    } finally {
      setIsUploading(false);
      // Limpa o input para permitir reenvio do mesmo arquivo
      e.target.value = '';
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8">
      <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-6">
        <User className="w-8 h-8 text-indigo-600" />
        Identidade do Funcionário
      </h3>
      
      <div className="space-y-6">
        {/* Nome do Funcionário */}
        <div>
          <label className="block text-lg font-semibold text-gray-700 mb-2">
            Nome do Funcionário <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onFieldChange('name', e.target.value, false)}
            className="w-full px-5 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all"
            placeholder="Ex: Maria - Atendente Virtual"
          />
        </div>

        {/* Foto do Funcionário */}
        <div>
          <label className="block text-lg font-semibold text-gray-700 mb-2">
            Foto do Funcionário
          </label>
          
          <div className="flex items-start gap-6">
            {/* Preview da imagem */}
            <div className="w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-indigo-100 to-blue-100 p-1 flex-shrink-0">
              <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
                {formData.logo_url ? (
                  <img 
                    src={formData.logo_url} 
                    alt="Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-full h-full flex items-center justify-center ${formData.logo_url ? 'hidden' : ''}`}
                >
                  <Bot className="w-14 h-14 text-indigo-400" />
                </div>
              </div>
            </div>
            
            {/* Área de upload/URL */}
            <div className="flex-1 space-y-3">
              {/* Seletor de modo */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setUploadMode('url')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    uploadMode === 'url'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Link2 className="w-4 h-4" />
                  URL
                </button>
                <button
                  onClick={() => setUploadMode('upload')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    uploadMode === 'upload'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload
                </button>
              </div>

              {/* Modo URL */}
              {uploadMode === 'url' && (
                <div>
                  <input
                    type="text"
                    value={formData.logo_url}
                    onChange={(e) => onFieldChange('logo_url', e.target.value, false)}
                    className="w-full px-5 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all"
                    placeholder="Cole a URL da imagem aqui"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Cole o link direto para uma imagem na internet
                  </p>
                </div>
              )}

              {/* Modo Upload */}
              {uploadMode === 'upload' && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full px-5 py-4 border-2 border-dashed border-indigo-300 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-all flex items-center justify-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                        <span className="text-indigo-600 font-medium">Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Image className="w-5 h-5 text-indigo-600 group-hover:scale-110 transition-transform" />
                        <span className="text-indigo-600 font-medium">
                          Clique para selecionar uma imagem
                        </span>
                      </>
                    )}
                  </button>
                  
                  <p className="text-sm text-gray-500 mt-2">
                    Formatos aceitos: JPEG, PNG, WebP, GIF (máx. 5MB)
                  </p>

                  {/* Mensagens de feedback */}
                  {uploadError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-red-700">{uploadError}</span>
                    </div>
                  )}

                  {uploadSuccess && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-green-700">Imagem enviada com sucesso!</span>
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-500">
                Use uma imagem quadrada para melhor resultado
              </p>
            </div>
          </div>
        </div>

        {/* Cores */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Cor do Funcionário
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.primaryColor}
                onChange={(e) => onFieldChange('primaryColor', e.target.value, false)}
                className="w-16 h-16 rounded-xl border-2 border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                value={formData.primaryColor}
                onChange={(e) => onFieldChange('primaryColor', e.target.value, false)}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Cor das Mensagens
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.secondaryColor}
                onChange={(e) => onFieldChange('secondaryColor', e.target.value, false)}
                className="w-16 h-16 rounded-xl border-2 border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                value={formData.secondaryColor}
                onChange={(e) => onFieldChange('secondaryColor', e.target.value, false)}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentIdentityCard;